import {
  LandingSection,
  LandingSectionContent,
  LandingSectionHeader,
  type LandingSectionProps,
  LandingSectionTitle,
} from "@/components/landing-page/landing-page-section";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import testimonials from "@/content/testimonials/testimonials.json";
import { cn } from "@/lib/utils";
import QuoteIcon from "lucide-solid/icons/quote";
import { type ComponentProps, splitProps } from "solid-js";

export function TestimonialsSection(props: LandingSectionProps) {
  const [local, rest] = splitProps(props, ["class"]);

  return (
    <LandingSection class={cn("", local.class)} {...rest}>
      <LandingSectionHeader>
        <LandingSectionTitle>Testimonials</LandingSectionTitle>
      </LandingSectionHeader>
      <LandingSectionContent class="min-h-[192px]">
        <Card class="border-border">
          <CardHeader class="relative w-full">
            <CardDescription class="font-semibold text-base text-muted-foreground/65 uppercase md:text-xl">
              Testimonals
            </CardDescription>
            <CardTitle class="text-lg leading-10 max-md:max-w-2/3 sm:text-xl md:text-3xl">
              <span class="font-medium text-base text-foreground/80 sm:text-lg md:text-2xl">
                Since 2020, we've worked with 250 coaches and facilitators
              </span>
              <span class="inline sm:hidden">.</span>
              <br />
              <span>Here's what they have to say</span>
            </CardTitle>
            <QuoteIcon class="-right-5 absolute top-1/3 size-1/3 rotate-12 text-muted-foreground/30" />
            <QuoteIcon class="-scale-x-100 absolute top-1/3 right-10 size-1/3 rotate-12 text-muted-foreground/30" />
          </CardHeader>
          <Separator class="border-border" />
          <CardContent class="bg-geometric pt-6">
            <div class="grid grid-cols-1 gap-x-10 gap-y-10 p-0 md:grid-cols-6 md:grid-rows-[1fr_1fr_min_1fr_1fr] md:gap-y-2 md:p-4 lg:gap-x-12">
              <TestimonialCard
                testimonial={testimonials[0]}
                class="col-span-full md:col-span-3 md:row-span-2"
              />
              <TestimonialCard
                testimonial={testimonials[4]}
                class="col-span-full md:col-span-3 md:col-start-4 md:row-span-3 md:row-start-2"
              />
              <TestimonialCard
                testimonial={testimonials[2]}
                class="col-span-full md:col-span-3 md:row-span-2 md:row-start-4"
              />
            </div>
          </CardContent>
        </Card>
      </LandingSectionContent>
    </LandingSection>
  );
}

function TestimonialCard(
  props: ComponentProps<"div"> & {
    testimonial: (typeof testimonials)[number];
  },
) {
  const [local, rest] = splitProps(props, ["class", "testimonial"]);

  return (
    <Card
      class={cn(
        "relative flex flex-col place-content-around text-pretty border-border p-4",
        local.class,
      )}
      {...rest}
    >
      <CardContent class="grow p-4">
        <p class="text-pretty text-left indent-4 font-serif text-foreground/80 text-lg">
          "{local.testimonial.testimonial}"
        </p>
      </CardContent>
      <CardHeader class="text-right md:text-center">
        <CardTitle class="text-pretty font-medium text-xl">
          {local.testimonial.name}
        </CardTitle>
        <CardDescription class="-mt-2 text-pretty text-muted-foreground/60 text-sm">
          {local.testimonial.title}
        </CardDescription>
      </CardHeader>
    </Card>
  );
}
